// types
import type {
  DealershipSelectGroup,
  DealershipSelectGroupBy,
  DealershipSelectOption,
  DealershipSelectSource
} from '@/types/domain/dealership'

/** Labels used when a dealership has no org, manufacturer, or tags */
interface DealershipSelectGroupLabels {
  /** Group name when manufacturer or org is missing */
  other: string
  /** Group name when grouping by tag and the dealership has none */
  untagged: string
}

/**
 * Normalize the list-endpoint `tags` array into unique trimmed names.
 * Accepts string tags or catalog objects with `title` or `name`.
 *
 * @param tags - Raw tags value from a dealership payload
 * @returns Unique tag names, ignoring empty or unknown entries
 */
export function normalizeDealershipTags(tags: unknown): string[] {
  if (!Array.isArray(tags)) {
    return []
  }

  const names: string[] = []

  tags.forEach(tag => {
    let name = ''

    // String tags from the list endpoint
    if (typeof tag === 'string') {
      name = tag.trim()
    } else if (tag && typeof tag === 'object') {
      // Catalog tags use `title`; some payloads use `name`
      const record = tag as { title?: unknown; name?: unknown }
      const rawName = typeof record.title === 'string' ? record.title : record.name
      name = typeof rawName === 'string' ? rawName.trim() : ''
    }

    // Skip blanks and duplicates so group headers stay clean
    if (name && !names.includes(name)) {
      names.push(name)
    }
  })

  return names
}

/**
 * Map a dealership `tags` payload onto catalog tag IDs for the settings selector.
 *
 * @param tags - Tags as returned by GET `/v1/dealerships/{id}`
 * @param catalog - Active dealership tags from GET `/v1/tags?for=dealership`
 * @returns Unique numeric tag IDs
 */
export function toDealershipTagIds(
  tags: unknown,
  catalog: Array<{ id: number; title?: string; name?: string }>
): number[] {
  if (!Array.isArray(tags)) {
    return []
  }

  const ids: number[] = []

  tags.forEach(tag => {
    let id: number | null = null

    if (typeof tag === 'number' && Number.isFinite(tag)) {
      id = tag
    } else if (typeof tag === 'string') {
      // Resolve title/name strings against the catalog once it has loaded
      const match = catalog.find(option => option.title === tag || option.name === tag)
      id = match?.id ?? null
    } else if (tag && typeof tag === 'object' && 'id' in tag) {
      const rawId = (tag as { id?: unknown }).id
      id = typeof rawId === 'number' && Number.isFinite(rawId) ? rawId : null
    }

    // Keep the selection unique so chips and PATCH stay stable
    if (id !== null && !ids.includes(id)) {
      ids.push(id)
    }
  })

  return ids
}

/**
 * Resolve the org display name from list, access-allowed, or nested org payloads.
 *
 * @param dealership - Dealership payload to read from
 * @returns Org / dealer-group name, or an empty string when missing
 */
function resolveOrgName(dealership: DealershipSelectSource): string {
  const nestedOrgName = dealership.org?.name

  return dealership.orgName || dealership.groupName || nestedOrgName || ''
}

/**
 * Map a dealership payload onto the option shape used by DealershipSelect.
 *
 * @param dealership - Dealership from the list endpoint or access store
 * @returns Option including org, manufacturer, and tags for search and grouping
 */
export function toDealershipSelectOption(dealership: DealershipSelectSource): DealershipSelectOption {
  const label = dealership.displayName || dealership.name || String(dealership.id)
  const orgName = resolveOrgName(dealership)
  const manufacturerName = dealership.manufacturerName || ''
  const tags = normalizeDealershipTags(dealership.tags)
  const searchText = [label, dealership.name, orgName, manufacturerName, ...tags]
    .filter(value => typeof value === 'string' && value.trim() !== '')
    .join(' ')

  return {
    value: dealership.id,
    label,
    orgName,
    manufacturerName,
    tags,
    searchText
  }
}

/**
 * Sort group labels alphabetically, keeping fallback buckets (Other / Untagged) last.
 *
 * @param labels - Group header labels to sort
 * @param trailingLabels - Labels that should always appear after named groups
 * @returns Sorted labels
 */
function sortGroupLabels(labels: string[], trailingLabels: string[]): string[] {
  return [...labels].sort((left, right) => {
    const leftIsTrailing = trailingLabels.includes(left)
    const rightIsTrailing = trailingLabels.includes(right)

    // Named groups first, then Other / Untagged
    if (leftIsTrailing !== rightIsTrailing) {
      return leftIsTrailing ? 1 : -1
    }

    return left.localeCompare(right)
  })
}

/**
 * Push a dealership into a group bucket, creating the bucket on first use.
 *
 * @param grouped - Mutable group map
 * @param groupLabel - Header to place the dealership under
 * @param option - Dealership option to add
 */
function addOptionToGroup(
  grouped: Map<string, DealershipSelectOption[]>,
  groupLabel: string,
  option: DealershipSelectOption
): void {
  const children = grouped.get(groupLabel)

  // First dealership in this group creates the bucket
  if (!children) {
    grouped.set(groupLabel, [option])
    return
  }

  children.push(option)
}

/**
 * Group dealership options for the shared Select.
 * Manufacturer and org are one-to-one. Tags are many-to-many: a dealership with
 * multiple tags appears under each matching tag so "select all" on a tag still works.
 *
 * @param options - Flat dealership options to group
 * @param groupBy - Active grouping mode
 * @param labels - Fallback labels for missing org / manufacturer / tags
 * @returns Groups sorted alphabetically, with fallback groups last
 */
export function groupDealershipsForSelect(
  options: DealershipSelectOption[],
  groupBy: DealershipSelectGroupBy,
  labels: DealershipSelectGroupLabels
): DealershipSelectGroup[] {
  const grouped = new Map<string, DealershipSelectOption[]>()
  const trailingLabels = [labels.other, labels.untagged]

  options.forEach(option => {
    if (groupBy === 'manufacturer') {
      addOptionToGroup(grouped, option.manufacturerName || labels.other, option)
      return
    }

    if (groupBy === 'org') {
      addOptionToGroup(grouped, option.orgName || labels.other, option)
      return
    }

    // Tag grouping: duplicate under every tag; untagged sites get their own bucket
    if (option.tags.length === 0) {
      addOptionToGroup(grouped, labels.untagged, option)
      return
    }

    option.tags.forEach(tag => {
      addOptionToGroup(grouped, tag, option)
    })
  })

  return sortGroupLabels([...grouped.keys()], trailingLabels).map(groupLabel => {
    const dealerships = grouped.get(groupLabel) ?? []

    return {
      groupLabel,
      dealerships: [...dealerships].sort((left, right) => left.label.localeCompare(right.label))
    }
  })
}

/**
 * Whether any option carries at least one tag (used to hide the Tag group-by mode).
 *
 * @param options - Dealership options to inspect
 * @returns True when at least one dealership has a tag
 */
export function dealershipOptionsHaveTags(options: DealershipSelectOption[]): boolean {
  return options.some(option => option.tags.length > 0)
}
